import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
/**
 * The shop-screen link, shown ONCE after it is made (customer-portal review #124).
 *
 * The link carries the counter key in its fragment — a capability, returned by the API exactly
 * once, like an API key. It used to be "held in this browser only" with no way to move it: no
 * copy button, no QR code, so it could not reach the counter tablet unless the console itself
 * was opened there. Now it is shown with Copy, a QR code the tablet's camera can read, and Open.
 *
 * The QR code is drawn here from the link — never fetched. Handing the link to a QR web service
 * would put the counter's capability in a third party's access log. `uqr` (MIT, no
 * dependencies; the operator console already uses it for TOTP enrolment) encodes it in the
 * browser, and the module matrix becomes one SVG path. Dark on light in both themes, with the
 * specification's four-module quiet zone, because a phone camera reads that most reliably.
 */
import { Button, Icon } from "@wetalk/ui";
import { useState, type ReactNode } from "react";
import { encode } from "uqr";

import { GLYPH } from "../../../../shell/glyph.js";

/** ISO/IEC 18004's quiet zone, in modules. */
const QUIET_ZONE = 4;

/** The dark modules as one path: a unit square per module, in module coordinates. */
export function shopLinkQrPathOf(text: string): { readonly size: number; readonly path: string } {
  const matrix = encode(text, { ecc: "M", border: QUIET_ZONE });
  let path = "";
  matrix.data.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) path += `M${String(String(x))} ${String(String(y))}h1v1h-1z`;
    });
  });
  return { size: matrix.size, path };
}

export interface ShopLinkPanelProps {
  /** The full link, capability in the fragment. */
  readonly url: string;
  readonly onOpen: () => void;
}

export function ShopLinkPanel({ url, onOpen }: ShopLinkPanelProps): ReactNode {
  usePortalTranslation("operations");
  const [copied, setCopied] = useState<"idle" | "copied" | "refused">("idle");
  const { size, path } = shopLinkQrPathOf(url);
  const box = `0 0 ${String(size)} ${String(size)}`;

  return (
    <div className="c-orders-link" role="region" aria-label={portalText("operations", "the_new_shop_screen_link_563c4ea7")}>
      <svg
        className="c-orders-qr"
        viewBox={box}
        role="img"
        aria-label={portalText("operations", "qr_code_of_the_shop_screen_link_scan_it_with_the_counter_t_7ae83056")}
        shapeRendering="crispEdges"
      >
        <rect className="c-orders-qr-light" x={0} y={0} width={size} height={size} />
        <path className="c-orders-qr-dark" d={path} />
      </svg>
      <div className="c-orders-link-body">
        <p className="c-orders-link-lede">
          {portalText("operations", "a_new_shop_screen_link_is_ready_and_the_previous_one_has_s_afd85901")}</p>
        <input className="c-orders-link-url" readOnly value={url} aria-label={portalText("operations", "shop_screen_link_d6229a94")} onFocus={(event) => {
          event.currentTarget.select();
        }} />
        <div className="c-row">
          <Button
            variant="secondary"
            onClick={() => {
              const clipboard = globalThis.navigator.clipboard as Clipboard | undefined;
              if (clipboard === undefined) {
                setCopied("refused");
                return;
              }
              clipboard.writeText(url).then(
                () => {
                  setCopied("copied");
                },
                () => {
                  setCopied("refused");
                },
              );
            }}
          >
            <Icon glyph={GLYPH.copy} size="md" />
            <span>{copied === "copied" ? portalText("operations", "copied_8e3df45a") : portalText("operations", "copy_the_link_ad85b6e9")}</span>
          </Button>
          <Button variant="primary" onClick={onOpen}>
            <Icon glyph={GLYPH.deviceTablet} size="md" />
            <span>{portalText("operations", "open_shop_screen_ea2eeaaa")}</span>
          </Button>
        </div>
        {copied === "refused" ? (
          <p className="c-orders-sub" role="alert">
            {portalText("operations", "this_browser_would_not_copy_it_select_the_link_above_and_c_d36671b6")}</p>
        ) : null}
      </div>
    </div>
  );
}
